import { getApiService } from '@infra/api/api-service.ts';
import { getCacheManager, generateCacheKey, AGRO_DASHBOARD_TTL_MS } from '@infra/cache/index.ts';
import type { ActivityUsageRankingItem } from '@core/types/activity.types.ts';
import type { ZoneByUsage } from '@core/types/zone.types.ts';

/**
 * Definiciones de las dos listas que el dashboard necesita antes de poder consultar
 * métricas: sin ellas no hay actividad ni zona por defecto que filtrar.
 *
 * Viven aquí, separadas de los hooks, para que el loader de la ruta pueda dispararlas
 * con `queryClient.prefetchQuery` mientras se descarga el chunk de la pantalla. Antes,
 * las dos peticiones no empezaban hasta que el componente montaba, y las tres consultas
 * pesadas esperaban a que terminaran: dos viajes de ida y vuelta en serie antes de
 * empezar lo caro. El hook y el prefetch comparten `queryKey`, así que el componente
 * encuentra el resultado ya en caché en vez de volver a pedirlo.
 */

const LIST_STALE_TIME_MS = 300_000;

export function companyActivitiesQuery(companyId: number) {
  return {
    queryKey: ['activities-usage-ranking', companyId] as const,
    queryFn: async (): Promise<ActivityUsageRankingItem[]> => {
      const cache = getCacheManager();
      const cacheKey = await generateCacheKey('/Activities/usage-ranking', {
        id_company: companyId,
      });

      const cached = await cache.get<ActivityUsageRankingItem[]>(cacheKey);
      if (cached) return cached;

      const data = await getApiService().getActivityUsageRanking(companyId);
      void cache.set(cacheKey, data, AGRO_DASHBOARD_TTL_MS);
      return data;
    },
    staleTime: LIST_STALE_TIME_MS,
  };
}

export function companyZonesQuery(companyId: number) {
  return {
    queryKey: ['zones-by-usage', companyId] as const,
    queryFn: async (): Promise<ZoneByUsage[]> => {
      const cache = getCacheManager();
      const cacheKey = await generateCacheKey('/Zones/company/by-usage', {
        companyId,
      });

      const cached = await cache.get<ZoneByUsage[]>(cacheKey);
      if (cached) return cached;

      const data = await getApiService().getZonesByCompanyUsage(companyId);
      void cache.set(cacheKey, data, AGRO_DASHBOARD_TTL_MS);
      return data;
    },
    staleTime: LIST_STALE_TIME_MS,
  };
}
